import { useReactFlow } from '@xyflow/react';
import type { NodeSettingsProps } from '../../types';
import {
  DEFAULT_WALKING_MOTION_ID, DEFAULT_WALKING_STEPS, SIDE_STEP_WALKING_MOTION_IDS, WALKING_MOTIONS, isSteppedWalkingMotion,
  normalizeWalkingData,
} from '../../config/walkingMotions';
import { useFlowDisplayLanguage } from '../../displayLanguage';
import { MAX_WALKING_STEPS } from '../../animation/walkingMotion';

const isValidSteps = (steps?: number): steps is number => (
  steps !== undefined && Number.isInteger(steps) && steps >= 1 && steps <= MAX_WALKING_STEPS
);

export function WalkingSettings({ node }: NodeSettingsProps<'walking'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const data = normalizeWalkingData(node.data);
  const motionId = data.motionId ?? DEFAULT_WALKING_MOTION_ID;
  const steps = data.stepCount;
  const stepped = isSteppedWalkingMotion(motionId);
  const invalidSteps = stepped && !isValidSteps(steps);
  const sideStep = SIDE_STEP_WALKING_MOTION_IDS.has(motionId);

  return (
    <div className="space-y-3">
      <label className="block" data-product-tour="card-input-1">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'walking motion' : '歩き方'}
        </span>
        <select
          value={motionId}
          onChange={(event) => {
            const nextId = event.target.value;
            updateNodeData(node.id, {
              motionId: nextId,
              stepCount: isSteppedWalkingMotion(nextId) ? (isValidSteps(steps) ? steps : DEFAULT_WALKING_STEPS) : undefined,
            });
          }}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal-400"
        >
          {WALKING_MOTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </label>
      {stepped && (
        <label className="block text-sm">
          {sideStep
            ? displayLanguage === 'en' ? 'Times' : '回数'
            : displayLanguage === 'en' ? 'Steps (excluding the closing motion)' : '歩数（最後に足を揃える動作は含めない）'}
          <input
            type="number"
            min={1}
            max={MAX_WALKING_STEPS}
            step={1}
            value={steps === undefined || steps === 0 ? '' : steps}
            onChange={(event) => updateNodeData(node.id, {
              motionId,
              stepCount: event.target.value === '' ? 0 : Number(event.target.value),
            })}
            aria-invalid={invalidSteps}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
          />
          {invalidSteps && (
            <span role="alert" className="text-red-600">
              {displayLanguage === 'en' ? `Enter an integer from 1 to ${MAX_WALKING_STEPS}.` : `1〜${MAX_WALKING_STEPS}の整数を入力してください。`}
            </span>
          )}
          {!sideStep && (
            <span className="mt-1 block text-slate-500">
              {motionId === '1010'
                ? displayLanguage === 'en' ? 'Starts with the right foot.' : '右足から開始します。'
                : displayLanguage === 'en' ? 'Starts with the left foot.' : '必ず左足から開始します。'}
            </span>
          )}
        </label>
      )}
    </div>
  );
}